import React, { useEffect, useState } from "react";
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { FilterValue } from "../../../../models/Filters/FilterValue";

type AppSearchDropDown2Props = {
  required?: boolean;
  isMultiSelect?: boolean;
  title: string;
  value?: FilterValue[];
  list: FilterValue[];
  local?: boolean;
  fontSize?: string;
  handleSearch?: (searchText?: string) => void;
  handleSelect?: (selectedItems: FilterValue[]) => void;
  disabled?: boolean;
  minWidth?: string;
  borderColor?: string;
  error?: boolean;
  errorMessage?: string;
  showBorder?: boolean;
  backgroundColor?: string;
  inputHeight?: string;
  chipSize?: string;
  showSelection?: boolean;
  borderRadius?: string;
  zIndex?: number;
};

export default function AppSearchDropDown2({
  required = false,
  isMultiSelect = false,
  title,
  list,
  value = [],
  local = false,
  fontSize = "0.8rem",
  handleSearch,
  handleSelect,
  disabled = false,
  minWidth,
  borderColor = "#ddd",
  error = false,
  errorMessage = "This is a required field",
  showBorder = true,
  showSelection = true,
  backgroundColor,
  inputHeight = "auto",
  chipSize = "100%",
  borderRadius = "8px",
  zIndex = 3000,
}: AppSearchDropDown2Props) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly FilterValue[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (!local) {
      onSearchComplete();
    } else {
      setOptions([...list]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, local]);

  const handleOpen = () => {
    if (!disabled) {
      setOpen(true);
      setLoading(false);
      setOptions([...list]);
    }
  };

  const onSearchComplete = () => {
    setLoading(false);
    setOptions([...list]);
  };

  const handleClose = () => {
    setOpen(false);
    setOptions([]);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value;
    setSearchText(searchValue);

    if (!open) return;

    if (local) {
      const PATTERN = new RegExp(searchValue, "i");
      const filteredValues = list.filter((item) => PATTERN.test(item.value));
      setOptions(filteredValues);
    } else {
      setLoading(true);
      handleSearch?.(searchValue);
    }
  };

  return (
    <Box sx={{ zIndex: 9000 }}>
      <Autocomplete
        sx={{
          padding: 0,
          margin: 0,
          borderRadius: borderRadius,
          minWidth: minWidth,
          fontSize: fontSize,
          height: inputHeight,
          "& .MuiOutlinedInput-root": {
            borderColor: showBorder
              ? `${error ? "#d32f2f" : borderColor}`
              : "none",
            borderWidth: showBorder ? "1px" : "0px",
            backgroundColor: disabled ? "#f5f5f5" : backgroundColor,
            borderRadius: borderRadius,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: showBorder
                ? error
                  ? "#d32f2f"
                  : borderColor
                : "transparent",
            },
            "&:hover fieldset": {
              borderColor: showBorder
                ? error
                  ? "#d32f2f"
                  : "#6455ED"
                : "transparent",
            },
            "&.Mui-focused fieldset": {
              borderColor: showBorder
                ? error
                  ? "#d32f2f"
                  : "#6455ED"
                : "transparent",
            },
          },
          "& .MuiInputLabel-root": {
            color: error ? "#d32f2f" : "#93969B",
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: error ? "#d32f2f" : "#6455ED",
          },
          "& .MuiInputBase-root": {
            // width: "100%",
            height: inputHeight,
          },
          "& .MuiChip-root": {
            maxWidth: chipSize,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        filterOptions={local ? undefined : (opts) => opts}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.value}
        options={options}
        loading={loading}
        noOptionsText={
          <Typography sx={{ fontSize: fontSize }}>No options</Typography>
        }
        loadingText={
          <Typography sx={{ fontSize: fontSize }}>Loading...</Typography>
        }
        inputValue={searchText}
        value={value}
        multiple={true}
        disabled={disabled}
        onChange={(event, value: FilterValue[]) => {
          if (handleSelect) {
            if (value) {
              if (isMultiSelect) {
                handleSelect(value);
              } else {
                handleSelect(
                  value.length === 0
                    ? []
                    : value.slice(value.length - 1, value.length),
                );
              }
              setSearchText("");
            }
          }
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input") {
            setSearchText(newInputValue);
          }
          if (newInputValue === "") {
            setLoading(false);
          }
        }}
        ChipProps={{
          sx: {
            fontSize: "0.8rem",
            height: "17.5px",
            fontWeight: 500,
            borderRadius: "4px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          size: "small",
        }}
        ListboxProps={{ sx: { fontSize: fontSize } }}
        slotProps={{
          popper: {
            sx: {
              zIndex,
            },
          },
        }}
        renderTags={(value, getTagProps) =>
          showSelection ? (
            value.map((option, index) => {
              const { key, ...restTagProps } = getTagProps({ index });
              return (
                <Chip
                  size="small"
                  label={option.value}
                  key={key}
                  sx={{
                    fontSize: "0.8rem",
                    height: "17.5px",
                    fontWeight: 500,
                    borderRadius: "4px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  {...restTagProps}
                />
              );
            })
          ) : (
            <Chip
              size="small"
              sx={{
                fontSize: "0.8rem",
                height: "17.5px",
                fontWeight: 500,
                borderRadius: "4px",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              label={value.length + " selected"}
            />
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              <Typography sx={{ fontSize: "0.8rem", color: "gray" }}>
                {title}
              </Typography>
            }
            required={required}
            size="small"
            onChange={handleInputChange}
            value={searchText}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={10} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
                style: {
                  fontSize: fontSize,
                  // paddingRight: isMultiSelect ? "3rem" : "3rem",
                },
              },
              inputLabel: {
                ...params.InputLabelProps,
                style: { fontSize: fontSize },
              },
            }}
          />
        )}
      />
      {error && (
        <Typography
          sx={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "4px" }}
        >
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
}
